import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type {
  Appointment,
  AppointmentStatus,
  SortField,
  SortOrder,
  GroupBy,
} from '@/types/appointments';

export interface AppointmentsState {
  // State
  items: Appointment[];
  loading: boolean;
  selectedDate: string;
  searchQuery: string;
  filterStatus: AppointmentStatus | 'all';
  filterEmployee: string;
  filterLocation: string;
  sortBy: SortField;
  sortOrder: SortOrder;
  groupBy: GroupBy;
  processedIds: string[];

  // Actions
  setItems: (items: Appointment[]) => void;
  setLoading: (loading: boolean) => void;
  setSelectedDate: (date: string) => void;
  setSearchQuery: (query: string) => void;
  setFilterStatus: (status: AppointmentStatus | 'all') => void;
  setFilterEmployee: (employeeId: string) => void;
  setFilterLocation: (locationId: string) => void;
  setSortBy: (sortBy: SortField) => void;
  setSortOrder: (sortOrder: SortOrder) => void;
  setGroupBy: (groupBy: GroupBy) => void;
  markProcessed: (id: string) => void;
  unmarkProcessed: (id: string) => void;
  isProcessed: (id: string) => boolean;
  clearProcessed: () => void;
}

function getTodayString(): string {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export const useAppointmentsStore = create<AppointmentsState>()(
  devtools(
    persist(
      (set, get) => ({
        items: [],
        loading: false,
        selectedDate: getTodayString(),
        searchQuery: '',
        filterStatus: 'all',
        filterEmployee: 'all',
        filterLocation: 'all',
        sortBy: 'time',
        sortOrder: 'asc',
        groupBy: 'none',
        processedIds: [],

        setItems: (items) => set({ items }),
        setLoading: (loading) => set({ loading }),
        setSelectedDate: (selectedDate) =>
          set((state) => ({
            selectedDate,
            items: state.selectedDate === selectedDate ? state.items : [],
          })),
        setSearchQuery: (searchQuery) => set({ searchQuery }),
        setFilterStatus: (filterStatus) => set({ filterStatus }),
        setFilterEmployee: (filterEmployee) => set({ filterEmployee }),
        setFilterLocation: (filterLocation) => set({ filterLocation }),
        setSortBy: (sortBy) => set({ sortBy }),
        setSortOrder: (sortOrder) => set({ sortOrder }),
        setGroupBy: (groupBy) => set({ groupBy }),

        markProcessed: (id) => {
          set((state) => {
            if (state.processedIds.includes(id)) return state;
            return { processedIds: [...state.processedIds, id] };
          });
        },

        unmarkProcessed: (id) => {
          set((state) => ({
            processedIds: state.processedIds.filter((pId) => pId !== id),
          }));
        },

        isProcessed: (id) => {
          return get().processedIds.includes(id);
        },

        clearProcessed: () => {
          set({ processedIds: [] });
        },
      }),
      {
        name: 'fresha-appointments',
        partialize: (state) => ({
          processedIds: state.processedIds,
          selectedDate: state.selectedDate,
          sortBy: state.sortBy,
          sortOrder: state.sortOrder,
          groupBy: state.groupBy,
          filterStatus: state.filterStatus,
          filterEmployee: state.filterEmployee,
          filterLocation: state.filterLocation,
        }),
      }
    ),
    { name: 'AppointmentsStore' }
  )
);
